import { useEffect, useRef, useState } from "react";
import {
  FaCheck,
  FaChevronDown,
  FaChevronLeft,
  FaChevronUp,
  FaCopy,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaPaperclip,
  FaPencilAlt,
  FaUpload,
  FaUserPlus,
} from "react-icons/fa";
import { IoIosClose } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import "./ClientInfo.css";
import NewRequestView from "../../../../components/NewRequestView/NewRequestView";

export default function ClientInfo({ onBack, selectedClient }) {
  const [activeTab, setActiveTab] = useState("Requests");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sdmEditMode, setSdmEditMode] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [sdmFormData, setSdmFormData] = useState({
    firstName: selectedClient?.sdm_first_name || "",
    middleName: selectedClient?.sdm_middle_name || "",
    lastName: selectedClient?.sdm_last_name || "",
    type: selectedClient?.sdm_type || "",
    relationship: selectedClient?.sdm_relationship || "",
    email: selectedClient?.sdm_email || "",
    document: null,
  });

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const filterOptions = [
    "Request Type",
    "Label",
    "Practitioner",
    "Request Date",
    "Status",
  ];

  const addFilter = (f) => {
    if (f && !selectedFilters.includes(f)) {
      setSelectedFilters([...selectedFilters, f]);
    }
  };

  const removeFilter = (f) =>
    setSelectedFilters(selectedFilters.filter((x) => x !== f));

  const handleRowClick = () => {
    navigate("/report", { state: { client: selectedClient } });
  };

  // close filter panel when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (
        filterOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [filterOpen]);

  // populate client info from prop or defaults
  const info = {
    firstName: selectedClient?.first_name || "Jane",
    middleName: selectedClient?.middle_name || "-",
    lastName: selectedClient?.last_name || "Doe",
    dateOfBirth: selectedClient?.dob?.split("T")[0] || "1990-01-01",
    phone: selectedClient?.phone || "(555) 123-4567",
    address: `${selectedClient?.address_line1 || "123 Maple St"} ${
      selectedClient?.address_line2 || ""
    }, ${selectedClient?.city || "Toronto"}, ${
      selectedClient?.province || "ON"
    }`.trim(),
    jurisdiction: selectedClient?.jurisdiction || "ON",
    healthCard: selectedClient?.health_card_number || "1234-567-890-AB",
    healthCode: "01", // no matching field
    sin: "987-654-321", // no matching field
    fileNumber: selectedClient?.file_number || "CLT-12345",
    groups: "Group A, Group B", // no matching field
    gender: selectedClient?.gender || "Female",
  };

  const isSdmAvailable = [
    selectedClient?.sdm_first_name,
    selectedClient?.sdm_last_name,
    selectedClient?.sdm_type,
    selectedClient?.sdm_relationship,
  ].every((value) => value && value.trim() !== "");

  const dummyRequests = [
    {
      id: "REQ-001",
      label: "Physiotherapy",
      practitioner: "Dr. Smith",
      date: "2023-04-01",
      status: "Complete",
    },
    {
      id: "REQ-002",
      label: "MRI Scan",
      practitioner: "Dr. Lee",
      date: "2023-04-15",
      status: "Awaiting Records",
    },
  ];

  const dummyRecords = [
    {
      filename: "ohip_history.pdf",
      type: "OHIP Claims",
      practitioner: "Ministry of Health",
      dates: "2019/02/28–2022/11/30",
      description: "OHIP Claims History",
      fileType: "pdf",
    },
    {
      filename: "xray_report.pdf",
      type: "X-Ray",
      practitioner: "Dr. Patel",
      dates: "2022/10/10",
      description: "Chest X-Ray",
      fileType: "pdf",
    },
    {
      filename: "lab_results.docx",
      type: "Lab Results",
      practitioner: "LifeLabs",
      dates: "2023/01/05",
      description: "Blood Test Results",
      fileType: "docx",
    },
  ];

  const handleSdmInputChange = (e) => {
    const { name, value } = e.target;
    setSdmFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setSdmFormData((prev) => ({
      ...prev,
      document: e.target.files[0],
    }));
  };

  const saveSdmInfo = () => {
    // Here you would typically make an API call to save the SDM info
    console.log("Saving SDM:", sdmFormData);

    // For demo purposes, we'll just toggle the edit mode off
    setSdmEditMode(false);
    // In a real app, you would update the selectedClient with the new SDM data
  };

  if (showNewRequest) {
    return (
      <NewRequestView
        client={selectedClient}
        onBack={() => setShowNewRequest(false)}
      />
    );
  }

  return (
    <div className="client-info-page">
      <button className="ci-back" onClick={onBack}>
        <FaChevronLeft /> Back to Clients List
      </button>
      <header className="ci-header">
        <h1>
          Client / {info.firstName} {info.lastName}
        </h1>
        <div className="ci-actions">
          <button className="ci-new" onClick={() => setShowNewRequest(true)}>
            New Request
          </button>
          <button className="ci-options">Options ▼</button>
        </div>
      </header>
      <section className="ci-card">
        <h2 className="ci-card-title">Client Information</h2>
        <div className="ci-grid">
          <div className="ci-field">
            <span className="ci-label">First Name</span>
            <span className="ci-value">{info.firstName}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Middle Name</span>
            <span className="ci-value">{info.middleName}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Last Name</span>
            <span className="ci-value">{info.lastName}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Date of Birth</span>
            <span className="ci-value">{info.dateOfBirth}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Gender</span>
            <span className="ci-value">{info.gender}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Phone Number</span>
            <span className="ci-value">{info.phone}</span>
          </div>
          <div className="ci-field ci-wide">
            <span className="ci-label">Address</span>
            <span className="ci-value">{info.address}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Health Card Jurisdiction</span>
            <span className="ci-value">{info.jurisdiction}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Canadian Health Card Number</span>
            <span className="ci-value">{info.healthCard}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">Health Card Code</span>
            <span className="ci-value">{info.healthCode}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">SIN Number</span>
            <span className="ci-value">{info.sin}</span>
          </div>
          <div className="ci-field">
            <span className="ci-label">File Number</span>
            <span className="ci-value">{info.fileNumber}</span>
          </div>
          <div className="ci-field ci-wide">
            <span className="ci-label">Groups</span>
            <span className="ci-value">{info.groups}</span>
          </div>
        </div>
      </section>
      <section className="ci-card">
        <h2 className="ci-card-title">Substitute Decision Maker Info</h2>

        {!isSdmAvailable && !sdmEditMode && (
          <div className="sdm-empty-state">
            <p>
              There is currently no Substitute Decision Maker (SDM) registered
              on this client profile.
            </p>
            <p>
              If your client requires an SDM (e.g., Parent/Guardian, Power of
              Attorney, Executor of Estate) to sign authorizations on the
              client's behalf, please register the SDM on the profile by
              clicking the button below. You will also be required to upload SDM
              documentation, which will be forwarded to providers to process
              your requests.
            </p>
            <button className="ci-new" onClick={() => setSdmEditMode(true)}>
              <FaUserPlus /> Add SDM
            </button>
          </div>
        )}

        {sdmEditMode && (
          <div className="sdm-edit-form">
            {isSdmAvailable && (
              <p className="sdm-edit-warning">
                If you edit the Substitute Decision Maker information below,
                please also ensure that any SDM document previously uploaded to
                this client profile is still correct.
              </p>
            )}

            <div className="sdm-form-grid">
              <div className="sdm-form-field">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={sdmFormData.firstName}
                  onChange={handleSdmInputChange}
                />
              </div>

              <div className="sdm-form-field">
                <label>Type *</label>
                <select
                  name="type"
                  value={sdmFormData.type}
                  onChange={handleSdmInputChange}
                >
                  <option value="">Select...</option>
                  <option value="Parent or Guardian">Parent or Guardian</option>
                  <option value="Power of Attorney">Power of Attorney</option>
                  <option value="Executor of Estate">Executor of Estate</option>
                </select>
              </div>

              <div className="sdm-form-field">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={sdmFormData.middleName}
                  onChange={handleSdmInputChange}
                />
              </div>

              <div className="sdm-form-field">
                <label>Relationship *</label>
                <input
                  type="text"
                  name="relationship"
                  value={sdmFormData.relationship}
                  onChange={handleSdmInputChange}
                />
              </div>

              <div className="sdm-form-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={sdmFormData.lastName}
                  onChange={handleSdmInputChange}
                />
              </div>

              <div className="sdm-form-field">
                <label>Contact Email</label>
                <input
                  type="email"
                  name="email"
                  value={sdmFormData.email}
                  onChange={handleSdmInputChange}
                />
              </div>
            </div>

            <div className="sdm-file-upload">
              <label>
                {isSdmAvailable
                  ? "Select file to upload and replace current SDM document"
                  : "Upload supporting document *"}
              </label>
              <div className="file-input-wrapper">
                <button className="file-input-button">
                  <FaPaperclip /> Choose File
                </button>
                <span className="file-input-text">
                  {sdmFormData.document
                    ? sdmFormData.document.name
                    : "No file chosen"}
                </span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="file-input"
                />
              </div>
            </div>

            <div className="sdm-form-actions">
              <button
                className="sdm-cancel"
                onClick={() => {
                  setSdmEditMode(false);
                  // Reset form if we were adding new SDM
                  if (!isSdmAvailable) {
                    setSdmFormData({
                      firstName: "",
                      middleName: "",
                      lastName: "",
                      type: "",
                      relationship: "",
                      email: "",
                      document: null,
                    });
                  }
                }}
              >
                Cancel
              </button>
              <button className="sdm-save" onClick={saveSdmInfo}>
                Save changes
              </button>
              {isSdmAvailable && (
                <button
                  className="sdm-remove"
                  onClick={() => {
                    // Here you would typically make an API call to remove the SDM
                    console.log("Removing SDM");
                    setSdmFormData({
                      firstName: "",
                      middleName: "",
                      lastName: "",
                      type: "",
                      relationship: "",
                      email: "",
                      document: null,
                    });
                    setSdmEditMode(false);
                  }}
                >
                  Remove SDM
                </button>
              )}
            </div>
          </div>
        )}

        {isSdmAvailable && !sdmEditMode && (
          <div className="sdm-view">
            <div className="sdm-details">
              <div className="sdm-detail">
                <label>Name</label>
                <span>
                  {selectedClient.sdm_first_name}{" "}
                  {selectedClient.sdm_middle_name}{" "}
                  {selectedClient.sdm_last_name}
                </span>
              </div>
              <div className="sdm-details-mid-section">
                <div className="sdm-detail">
                  <label>Type</label>
                  <span>{selectedClient.sdm_type}</span>
                </div>
                <div className="sdm-detail">
                  <label>Relationship</label>
                  <span>{selectedClient.sdm_relationship}</span>
                </div>
                <div className="sdm-detail">
                  <label>Contact Email</label>
                  <span>{selectedClient.sdm_email || "N/A"}</span>
                </div>
              </div>
              <div className="sdm-detail">
                <label>Supporting Document</label>
                <div>
                  {selectedClient.sdm_document ? (
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      View Document
                    </a>
                  ) : (
                    <div
                      className={
                        selectedClient.sdm_document ? "" : "sdm-missing-doc"
                      }
                    >
                      <FaExclamationTriangle /> Missing - Press Edit to upload
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button className="sdm-edit" onClick={() => setSdmEditMode(true)}>
              <FaPencilAlt /> Edit SDM
            </button>
          </div>
        )}
      </section>
      <section className="ci-card">
        <div className="ci-tabs">
          {["Requests", "Records", "Summarization Orders"].map((t) => (
            <button
              key={t}
              className={t === activeTab ? "ci-tab active" : "ci-tab"}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {activeTab === "Requests" && (
          <>
            <div className="ci-filters-row">
              <div className="ci-filter-container" ref={dropdownRef}>
                <div
                  className="ci-filter-trigger"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <span className="ci-filter-trigger-left">
                    {selectedFilters.length === 0 ? (
                      <span className="ci-placeholder">Select Filters…</span>
                    ) : (
                      selectedFilters.map((f) => (
                        <span key={f} className="ci-chip-inline">
                          {f}
                          <IoIosClose
                            className="ci-chip-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFilter(f);
                            }}
                          />
                        </span>
                      ))
                    )}
                  </span>
                  <span className="ci-filter-icons">
                    <IoIosClose
                      size={20}
                      className="ci-filter-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFilters([]);
                      }}
                    />
                    |
                    {filterOpen ? (
                      <FaChevronUp className="ci-filter-icon" />
                    ) : (
                      <FaChevronDown className="ci-filter-icon" />
                    )}
                  </span>
                </div>
                {filterOpen && (
                  <div className="ci-filter-panel">
                    {filterOptions.map((opt) => (
                      <label key={opt} className="ci-filter-item">
                        <input
                          type="checkbox"
                          checked={selectedFilters.includes(opt)}
                          onChange={() =>
                            selectedFilters.includes(opt)
                              ? removeFilter(opt)
                              : addFilter(opt)
                          }
                        />
                        {opt}
                      </label>
                    ))}
                    <button
                      className="ci-filter-apply"
                      onClick={() => setFilterOpen(false)}
                    >
                      <FaCheck /> Apply
                    </button>
                  </div>
                )}
              </div>

              <div className="ci-search-group">
                <input className="ci-search" placeholder="Search" />
                <button className="ci-clear">Clear</button>
              </div>
            </div>

            <table className="ci-table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Label</th>
                  <th>Practitioner</th>
                  <th>Request Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {dummyRequests.map((r) => (
                  <tr key={r.id} className="ci-row" onClick={handleRowClick}>
                    <td>{r.id}</td>
                    <td>{r.label}</td>
                    <td>{r.practitioner}</td>
                    <td>{r.date}</td>
                    <td>{r.status}</td>
                    <td className="ci-action-icons">
                      <FaExternalLinkAlt /> <FaCopy />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ci-pagination">
              <label>
                Show{" "}
                <select defaultValue="10">
                  <option>10</option>
                  <option>25</option>
                  <option>50</option>
                </select>{" "}
                entries
              </label>
            </div>
          </>
        )}

        {activeTab === "Records" && (
          <>
            <button className="ci-upload">
              <FaUpload /> Upload
            </button>
            <div className="ci-record-search">
              <input className="ci-search" placeholder="Search records" />
              <button className="ci-clear">Clear</button>
            </div>
            <table className="ci-table">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Type</th>
                  <th>Practitioner</th>
                  <th>Record Date(s)</th>
                  <th>Description</th>
                  <th>File Type</th>
                </tr>
              </thead>
              <tbody>
                {dummyRecords.map((r) => (
                  <tr key={r.filename} className="ci-row">
                    <td>{r.filename}</td>
                    <td>{r.type}</td>
                    <td>{r.practitioner}</td>
                    <td>{r.dates}</td>
                    <td>{r.description}</td>
                    <td>{r.fileType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ci-pagination">
              <label>
                Show{" "}
                <select defaultValue="10">
                  <option>10</option>
                  <option>25</option>
                  <option>50</option>
                </select>{" "}
                entries
              </label>
            </div>
          </>
        )}

        {activeTab === "Summarization Orders" && (
          <div className="ci-placeholder-text">
            No summarization orders available.
          </div>
        )}
      </section>
    </div>
  );
}
